// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Step 6: Confirm and lock step component.
 *
 * Final confirmation and period lock with signature.
 */
import {
  CloseRunResponse,
  JournalEntryWithReviewState,
  PriorPeriodAdjustment,
  SavedAccountingPeriodActivityStatus,
  WorkflowReviewState,
} from '@/accounting/api/types';
import { ReviewStatusChip } from '@/accounting/components/review-status-chip';
import { isPendingReview } from '@/accounting/utilities/is-pending-review';
import { toPerCurrencySectionBuckets } from '@/accounting/utilities/to-per-currency-section-buckets';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import {
  CheckOutlined,
  ChevronLeftRounded,
  CircleOutlined,
  LockRounded,
  PublishedWithChangesOutlined,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';

interface Props {
  period: SavedAccountingPeriodActivityStatus;
  closeRun: CloseRunResponse | null;
  reviewState: WorkflowReviewState;
  onBack: () => void;
  onLock: (signature: string, notes: string) => Promise<boolean>;
}

/**
 * Renders a single line in the review summary showing the status of a journal entry.
 */
function ReviewSummaryItem(props: {
  entry: JournalEntryWithReviewState;
  label: string;
}) {
  const hasLines = props.entry.lineItems.length > 0;
  const isReviewed = props.entry.reviewed;

  if (!hasLines) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ width: '100%' }}
      >
        <Typography variant="body1" color="text.secondary">
          {props.label}
        </Typography>
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            mx: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontFamily: 'monospace',
              letterSpacing: '0.2em',
            }}
          >
            {'.'.repeat(200)}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          None
        </Typography>
        <CircleOutlined color="disabled" />
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ width: '100%' }}
    >
      <Typography variant="body1">{props.label}</Typography>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          mx: 1,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontFamily: 'monospace',
            letterSpacing: '0.2em',
          }}
        >
          {'.'.repeat(200)}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        color={isReviewed ? 'success.main' : 'primary.main'}
      >
        {isReviewed ? 'Reviewed' : 'Pending'}
      </Typography>
      {isReviewed ? (
        <CheckOutlined color="success" />
      ) : (
        <PublishedWithChangesOutlined color="primary" />
      )}
    </Stack>
  );
}

/**
 * Returns the appropriate color for a delta value.
 * Non-zero deltas are shown in warning color.
 */
function getDeltaColor(delta: string): 'warning.main' | 'text.primary' {
  const numericDelta = parseFloat(delta);
  return numericDelta !== 0 ? 'warning.main' : 'text.primary';
}

/**
 * Renders a single row in the period locks table.
 */
function PeriodLockRow(props: {
  adjustment: PriorPeriodAdjustment;
  currencyCode: IsoCurrencyCode;
}) {
  const { adjustment } = props;
  const numericDelta = parseFloat(adjustment.delta);
  const hasNonZeroDelta = numericDelta !== 0;

  return (
    <TableRow>
      <TableCell>{adjustment.accountName}</TableCell>
      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
        {formatAmountWithCurrency(adjustment.priorBalance, props.currencyCode, {
          sparse: true,
        })}
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
        {formatAmountWithCurrency(
          adjustment.currentBalance,
          props.currencyCode,
          {
            sparse: true,
          }
        )}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontFamily: 'monospace',
          color: getDeltaColor(adjustment.delta),
          fontWeight: hasNonZeroDelta ? 'bold' : 'normal',
        }}
      >
        {formatAmountWithCurrency(adjustment.delta, props.currencyCode, {
          sparse: true,
        })}
      </TableCell>
    </TableRow>
  );
}

export function ConfirmLockStep(props: Props) {
  const [signature, setSignature] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState('');
  const [isLocking, setIsLocking] = useState(false);

  const allEntries = [
    ...props.reviewState.inventoryEntries,
    ...props.reviewState.fixedAssetEntries,
    ...props.reviewState.adjustmentEntries,
  ];
  const allEntriesReviewed = !allEntries.some(isPendingReview);

  const canLock = signature.trim() !== '' && confirmed && allEntriesReviewed;

  const handleLock = async () => {
    setIsLocking(true);
    try {
      await props.onLock(signature, notes);
    } finally {
      setIsLocking(false);
    }
  };

  const getTooltipTitle = () => {
    if (!allEntriesReviewed) {
      return 'Please review all journal entries before proceeding';
    }
    if (!confirmed) {
      return 'Please confirm that all journal entries have been posted';
    }
    if (signature.trim() === '') {
      return 'Please enter your signature before proceeding';
    }
    return '';
  };

  const periodStartDate = new Date(
    props.period.year,
    props.period.month - 1,
    1
  );
  const periodEndDate = DateTime.fromJSDate(periodStartDate).endOf('month');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Lock button - top */}
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 1 }}
      >
        <Tooltip arrow title={!canLock && getTooltipTitle()}>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<LockRounded fontSize="small" />}
              onClick={handleLock}
              disabled={!canLock || isLocking}
            >
              {isLocking ? 'Locking period...' : 'Lock period'}
            </Button>
          </Box>
        </Tooltip>
      </Box>

      {allEntriesReviewed ? (
        <Alert severity="info">All journal entry reviews complete</Alert>
      ) : (
        <Alert severity="warning">
          Some journal entries have not been reviewed
        </Alert>
      )}

      <Card variant="outlined">
        <CardHeader
          title="Period review"
          subheader={`As of ${periodEndDate.toLocaleString(DateTime.DATE_FULL)}`}
          sx={{ pt: 3, paddingBottom: 1 }}
        />
        <CardContent sx={{ paddingX: 3 }}>
          <PerCurrencySections
            buckets={toPerCurrencySectionBuckets(
              props.closeRun?.byCurrency ?? []
            )}
            emptyMessage="No data for this period"
            prominentHeader
            defaultExpanded={() => false}
            renderHeaderChip={(bucket) => (
              <ReviewStatusChip
                isReviewed={
                  !allEntries.some(
                    (entry) =>
                      entry.currency === bucket.currency &&
                      isPendingReview(entry)
                  )
                }
              />
            )}
            renderBucket={(bucket) => (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Journal entry summary
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {allEntries
                      .filter((entry) => entry.currency === bucket.currency)
                      .map((entry) => (
                        <ReviewSummaryItem
                          key={entry.id}
                          entry={entry}
                          label={entry.externalId}
                        />
                      ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Period locks computed
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Account</TableCell>
                          <TableCell align="right">
                            Prior period balance
                          </TableCell>
                          <TableCell align="right">
                            Current period balance
                          </TableCell>
                          <TableCell align="right">Difference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bucket.adjustments.map((adjustment) => (
                          <PeriodLockRow
                            key={adjustment.accountType}
                            adjustment={adjustment}
                            currencyCode={bucket.currency}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Stack>
            )}
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title="Confirm and lock period"
          subheader={periodStartDate.toLocaleString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
          sx={{ paddingBottom: 0 }}
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', px: 1, gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  size="small"
                />
              }
              slotProps={{ typography: { variant: 'body2' } }}
              label={`I confirm that I have reviewed all journal entries and account balances.
                Journal entries have been posted and approved in the company's general ledger.
                I understand that locking this period will prevent further modifications to the accounts listed above for ${periodStartDate.toLocaleString(
                  'en-US',
                  {
                    month: 'long',
                    year: 'numeric',
                  }
                )}.`}
            />

            <TextField
              size="small"
              label="Signature (required)"
              placeholder="Enter your full name"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              fullWidth
              required
            />

            <TextField
              size="small"
              label="Notes (optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
            />

            <Alert severity="warning" icon={<WarningAmberOutlined />}>
              <AlertTitle>
                Locking this period will freeze all Hardfin balances as of{' '}
                {periodEndDate.toLocaleString(DateTime.DATE_FULL)}
              </AlertTitle>
              Any subsequent operational changes to prior periods will be
              reflected during the next close as prior-period adjustments.
            </Alert>
          </Box>
        </CardContent>
      </Card>

      {/* Navigation buttons - bottom */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 1,
          marginBottom: 3,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<ChevronLeftRounded fontSize="small" />}
          onClick={props.onBack}
          disabled={isLocking}
        >
          Back
        </Button>
        <Tooltip arrow title={!canLock && getTooltipTitle()}>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<LockRounded fontSize="small" />}
              onClick={handleLock}
              disabled={!canLock || isLocking}
            >
              {isLocking ? 'Locking period...' : 'Lock period'}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
